import { Flex, Pagination, Dropdown, Divider, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useState, useMemo, memo } from "react";

import { ReactComponent as ArrowDown } from "../../images/table/Arrow-down.svg";
import { ReactComponent as ArrowLeft } from "../../images/table/Arrow-left.svg";
import { ReactComponent as ArrowRight } from "../../images/table/Arrow-right.svg";
import { PlateListSimple } from "./PlateListSimple";
import styles from "./plateList.module.css";

type PlateListSimpleProps<T, S> = Parameters<typeof PlateListSimple<T, S>>[0];

interface PlateListWithPaginationProps<T, S> extends PlateListSimpleProps<T, S> {
  store: S &
    PlateListSimpleProps<T, S>["store"] & {
      paginationData?: {
        currentPage?: number;
        pageSize?: number;
        totalElements?: number;
      };
      onChangeDataFlow?: (params: {
        paginationData: {
          currentPage: number;
          pageSize: number;
        };
      }) => void;
    };
  pageSizeOptions?: number[];
}

const PageSizeMenu = memo(
  ({
    pageSize,
    pageSizeOptions,
    pageSizeDropdownVisible,
    onOpenChange,
    onSizeChange,
  }: {
    pageSize: number;
    pageSizeOptions: number[];
    pageSizeDropdownVisible: boolean;
    onOpenChange: (visible: boolean) => void;
    onSizeChange: (value: number) => void;
  }) => {
    const pageSizeMenuItems = useMemo(
      () =>
        pageSizeOptions.map((size) => ({
          key: size,
          label: size,
          onClick: () => onSizeChange(size),
        })),
      [pageSizeOptions, onSizeChange]
    );

    return (
      <div className={styles.pageSizeChanger}>
        <Dropdown
          menu={{ items: pageSizeMenuItems }}
          trigger={["click"]}
          open={pageSizeDropdownVisible}
          onOpenChange={onOpenChange}
          destroyPopupOnHide={false}
        >
          <Flex gap={"15px"} align="center">
            <Typography.Text strong>Показывать по </Typography.Text>
            <Flex gap={"small"} className={styles.pageSizeChangerClickableArea}>
              <Typography.Text strong className={styles.valueText}>
                {pageSize}
              </Typography.Text>
              <ArrowDown className={styles.arrowIcon} />
            </Flex>
          </Flex>
        </Dropdown>
      </div>
    );
  }
);

export const PlateListWithPagination = observer(
  <T, S>({
    store,
    columns,
    PlateComponent,
    updateWithInterval,
    errorMessage,
    className,
    showHeader,
    onRow,
    headerItemComponent,
    footerItemComponent,
    checkbox,
    pageSizeOptions = [10, 20, 50, 100],
    emptyProps,
  }: PlateListWithPaginationProps<T, S>) => {
    const [pageSizeDropdownVisible, setPageSizeDropdownVisible] = useState<boolean>(false);
    const pageSizeDefault = pageSizeOptions[0];

    const pageSize = store.paginationData?.pageSize || pageSizeDefault;
    const currentPage = store.paginationData?.currentPage || 1;
    const totalElements = store.paginationData?.totalElements || 0;

    const handlePageChange = (page: number, size?: number) => {
      if (store.onChangeDataFlow) {
        store.onChangeDataFlow({
          paginationData: {
            currentPage: page,
            pageSize: size || pageSize,
          },
        });
      }
    };

    const handlePageSizeChange = (value: number) => {
      if (store.onChangeDataFlow) {
        store.onChangeDataFlow({
          paginationData: {
            currentPage: 1,
            pageSize: value,
          },
        });
      }
    };

    const handlePageSizeVisibleChange = (visible: boolean) => {
      setPageSizeDropdownVisible(visible);
    };

    const pageSizeMenuMemoized = useMemo(
      () => (
        <PageSizeMenu
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          pageSizeDropdownVisible={pageSizeDropdownVisible}
          onOpenChange={handlePageSizeVisibleChange}
          onSizeChange={handlePageSizeChange}
        />
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [pageSize, pageSizeDropdownVisible]
    );

    return (
      <Flex vertical gap="middle">
        <PlateListSimple
          store={store}
          columns={columns}
          PlateComponent={PlateComponent}
          updateWithInterval={updateWithInterval}
          errorMessage={errorMessage}
          emptyProps={emptyProps}
          className={className}
          showHeader={showHeader}
          onRow={onRow}
          headerItemComponent={headerItemComponent}
          footerItemComponent={footerItemComponent}
          checkbox={
            checkbox && {
              getCheckboxProps: checkbox.getCheckboxProps,
            }
          }
        />
        {store.paginationData && totalElements > pageSizeDefault && (
          <Flex vertical gap="middle">
            <Divider className={"margin-top-none margin-bottom-none"} />
            <Flex justify="space-between" align="center" className={"full-width"}>
              {totalElements > 0 && pageSizeMenuMemoized}
              <Pagination
                current={currentPage}
                total={totalElements}
                pageSize={pageSize}
                showSizeChanger={false}
                onChange={handlePageChange}
                className={styles.pagination}
                prevIcon={<ArrowLeft />}
                nextIcon={<ArrowRight />}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    );
  }
);
